import {
  Contact,
  ContactEventEntity,
  ContactParticipation,
  ContactStatusType,
  ContactSummary,
  FieldType,
  Utterance,
  Value,
} from "@/services/api/sdk";
import { ContactEntry } from "@/services/api/sdk/models/ContactEntry.ts";
import { format } from "date-fns";

export enum CallEventType {
  UTTERANCE = "Utterance",
  PARTICIPATION = "Participation",
  EVENT = "Event",
}

export type IDataEntryMode = "AUTO" | "MANUAL";

export type ContactAgentMeta = {
  id: number;
  firstName: string;
  lastName: string;
};

export interface ContactUtteranceType {
  type: CallEventType.UTTERANCE;
  date: Date;
  partialId?: string;
  showTime?: boolean;
  agent?: ContactAgentMeta;
}

export interface ContactParticipationType {
  type: CallEventType.PARTICIPATION;
  date: Date;
  agent?: ContactAgentMeta;
}

export interface ContactEventType {
  type: CallEventType.EVENT;
  date: Date;
  agent?: ContactAgentMeta;
}

export type ContactUtteranceEvent = ContactUtteranceType & Utterance;
export type ContactParticipationEvent = ContactParticipationType &
  ContactParticipation;
export type ContactEvent = ContactEventType & ContactEventEntity;
export type ContactTimelineEvent =
  | ContactUtteranceEvent
  | ContactParticipationEvent
  | ContactEvent;
export type ContactTimelineData =
  | Utterance
  | ContactParticipation
  | ContactEventEntity;

export enum ICallStatus {
  IDLE = "IDLE",
  INCOMING = "INCOMING",
  OUTBOUND = "OUTBOUND",
  CONNECTING = "CONNECTING",
  ACTIVE = "ACTIVE",
  ENDED = "ENDED",
  ERROR = "ERROR",
}

export interface ICallState {
  status: ICallStatus;
  isMuted: boolean;
}

export function standardiseTimelineEvents({
  participations,
  utterances,
}: {
  participations: ContactParticipation[];
  utterances: Utterance[];
}): ContactTimelineEvent[] {
  return [
    ...participations.map((participation) => {
      return {
        type: CallEventType.PARTICIPATION,
        date: participation.dateTransferred,
        ...participation,
      } as ContactTimelineEvent;
    }),
    ...utterances.map((utterance) => {
      return {
        type: CallEventType.UTTERANCE,
        date: utterance.dateStarted,
        ...utterance,
      } as ContactTimelineEvent;
    }),
  ];
}

export function buildCallEventsTimeline(events: ContactTimelineEvent[]) {
  /*const sortedEvents = events.sort((a, b) => {
        return a.date.getTime() >= b.date.getTime() ? 1 : -1;
    });*/

  const sortedEvents = events
    .filter((e) => e.type != CallEventType.EVENT)
    .sort((a, b) => {
      // Compare by date
      return a.date.getTime() - b.date.getTime();
    }) as ContactTimelineEvent[];

  // inject events right before utterance
  events
    .filter((e) => e.type == CallEventType.EVENT)
    .forEach((event) => {
      const utterance = sortedEvents.find((e) => e.id === event.utteranceId);
      if (utterance) {
        const index = sortedEvents.indexOf(utterance);
        sortedEvents.splice(index, 0, event);
      }
    });

  return sortedEvents.map((event, index) => {
    if (event.type === CallEventType.UTTERANCE) {
      const previousEvent = sortedEvents[index - 1];
      return {
        ...event,
        showTime:
          !previousEvent ||
          previousEvent.type !== event.type ||
          previousEvent.participantRole !== event.participantRole,
      };
    }

    return event;
  });
}

function formatTimeUnit(unit: number): string {
  return unit.toString().padStart(2, "0");
}

export function longFormatCallDuration(duration: number) {
  const hours = Math.floor((Math.abs(duration) / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((Math.abs(duration) / (1000 * 60)) % 60);
  const seconds = Math.floor((Math.abs(duration) / 1000) % 60);
  const durationString: string[] = [];

  if (hours > 0) {
    durationString.push(`${hours}hours`);
  }
  durationString.push(`${minutes}min`);
  durationString.push(`${formatTimeUnit(seconds)}sec`);
  return durationString.join(" ");
}

export function formatCallDuration(duration: number) {
  const hours = Math.floor((Math.abs(duration) / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((Math.abs(duration) / (1000 * 60)) % 60);
  const seconds = Math.floor((Math.abs(duration) / 1000) % 60);
  const units: number[] = [];

  if (hours > 0) {
    units.push(hours);
  }
  units.push(minutes);
  units.push(seconds);

  return units.map((unit) => formatTimeUnit(unit)).join(":");
}

export function getCallStartTime(
  call: Contact | ContactSummary,
): string | null {
  const startedEvent = call.statusHistory?.find(
    (item) => item.status === ContactStatusType.Started,
  );

  if (startedEvent) {
    return format(startedEvent.dateCreated!, "HH:mm");
  }

  return null;
}

export function getCallEndTime(call: Contact | ContactSummary): string | null {
  const startedEvent = call.statusHistory?.find(
    (item) => item.status === ContactStatusType.Ended,
  );

  if (startedEvent) {
    return format(startedEvent.dateCreated!, "HH:mm");
  }

  return null;
}

export function getCallLength(
  call: Contact | ContactSummary,
  formatOutputFn: (duration: number) => string = formatCallDuration,
) {
  const startedEvent = call.statusHistory?.find(
    (item) => item.status === ContactStatusType.Started,
  );
  const endedEvent = call.statusHistory?.find(
    (item) => item.status === ContactStatusType.Ended,
  );
  if (!startedEvent || !endedEvent) {
    return null;
  }
  const diff =
    startedEvent.dateCreated!.getTime() - endedEvent.dateCreated!.getTime();

  return formatOutputFn(diff);
}

export function getCallDuration(call: Contact, now: Date) {
  const startedEvent = call.statusHistory!.find(
    (item) => item.status === ContactStatusType.Started,
  );
  const dateStarted = startedEvent!.dateCreated!;

  const diff = now.getTime() - dateStarted.getTime();
  return formatCallDuration(diff);
}

function formatUSPhoneNumber(phoneNumber: string): string {
  return phoneNumber.replace(/(\+1)?(\d{3})(\d{3})(\d{4})/, (_, a, b, c, d) => {
    return `${a}(${b}) ${c}-${d}`;
  });
}

export function formatValue(
  value: Value,
  opts?: { fieldType?: FieldType; locale?: string },
): string {
  if (typeof value.valueString !== "undefined") {
    if (opts?.fieldType === FieldType.Date) {
      return new Intl.DateTimeFormat(opts?.locale || "en-US").format(
        new Date(value.valueString),
      );
    }

    if (opts?.fieldType === FieldType.Phone) {
      return formatUSPhoneNumber(value.valueString);
    }

    return value.valueString;
  }

  if (value.valueNumber) {
    return value.valueNumber!.toString();
  }
  return value.valueBoolean ? "Yes" : "No";
}

export function getCurrentCallStatus(call: Contact): ContactStatusType {
  const { status } = call.statusHistory!.sort(
    (a, b) => b.dateCreated!.getTime() - a.dateCreated!.getTime(),
  )[0];
  return status!;
}

export function sortContactEntriesByField(a: ContactEntry, b: ContactEntry) {
  return a.fieldId! > b.fieldId! ? 1 : -1;
}

export function getContactTimezone(contact: Contact) {
  const date = new Date();
  const formatter = new Intl.DateTimeFormat(contact.locale, {
    timeZone: contact.timezone,
    timeZoneName: "long",
  });

  const [_time, timezone] = formatter.format(date).split(", ");
  return timezone.trim();
}
